import { Fragment, FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { v4 as uuidv4 } from "uuid";

import { faDownload } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ApiResponse, InitOverrideFunction, SignalREventNames } from "../../../../@api";

import { blobFromByteArray, TMimeType } from "../../../../utils/functions/createBlobFromBytes";
import { fieldNonexistentOrMatchingFilter, FileUploadComponent, IFileType } from "../../../components/Shared";
import { ProgressCircle } from "../../../components/Shared/ProgressHandler/ProgressCircle/ProgressCircle";
import {
  IProgressColumn,
  IProgressVisualizationProps,
  ProgressHandler,
} from "../../../components/Shared/ProgressHandler/ProgressHandler";
import { translationKeys } from "../../../models/app-translation-keys";
import {
  IFileProgressError,
  IFileProgressSuccess,
  ILanguageOption,
  IProgressedFileResult,
} from "../../../models/interfaces";
import { useTranslationService } from "../../../services/ServiceHooks";
import { useStore } from "../../../stores/StoreContext";
import { TranslationContentLayout } from "../TranslationContentLayout/TranslationContentLayout";
import { IGlossaryFileSupportExtended } from "../TranslationHeader/GlossarySelection/GlossarySelection.types";
import { TranslationType } from "../TranslationHeader/TranslationHeader";

import "./TranslationContentFile.scss";

import { possibleFileTypes } from "../../../components/Shared/Models/Constants/wellKnownFileTypes.const";

interface ITranslationContentFileProps {
  sourceLanguage: ILanguageOption;
  targetLanguage: ILanguageOption;
  selectedGlossary: IGlossaryFileSupportExtended | null;
}

interface IProgressedTranslationFileResult extends IProgressedFileResult {
  outputFile?: Blob;
}

const fileTranslationKeys = translationKeys.feature.translation.type.file;

const allowedFileTypes: IFileType[] = [
  possibleFileTypes.pdf,
  possibleFileTypes.doc,
  possibleFileTypes.docx,
  possibleFileTypes.txt,
  possibleFileTypes.xlsx,
  possibleFileTypes.html,
  possibleFileTypes.pptx,
];

export const TranslationContentFile: FunctionComponent<ITranslationContentFileProps> = observer(
  (props: ITranslationContentFileProps) => {
    const { t } = useTranslation();
    const { budgetStore, translationStore } = useStore();
    const translationService = useTranslationService();

    const [isSignalRActive, setIsSignalRActive] = useState<boolean>(false);
    const [progressedFileResult, setProgressedFileResult] = useState<IProgressedTranslationFileResult | undefined>(
      undefined,
    );
    const [isFileProcessing, setIsFileProcessing] = useState<boolean>(false);

    const processIdRef = useRef<string | null>(null);
    const abortControllerRef = useRef<AbortController | undefined>(undefined);
    const translationInProgressRef = useRef<boolean>(false);

    const uploadFile = translationStore.uploadFiles[0];

    useEffect(() => {
      if (!isFileProcessing && translationStore.containsProgressingFile(uploadFile)) {
        translationStore.removeProgressingFile(uploadFile);
      }
    }, [isFileProcessing]);

    useEffect(() => {
      if (isFileProcessing && isSignalRActive) {
        startUpload();
      }
    }, [isFileProcessing, isSignalRActive]);

    useEffect(() => {
      // Cleanup function to cancel ongoing file upload and stop open signalR connections before unmounting
      return () => {
        cancelFileUploadAndOpenProcesses();
      };
    }, []);

    const handleBudgetLimitReached = useCallback((response: Response) => {
      budgetStore.setBudgetDisabled(true);
      budgetStore.errorCode = response.status;
    }, []);

    const setDefaultProgressValues = () => {
      processIdRef.current = null;
      translationInProgressRef.current = false;
      setIsFileProcessing(false);
    };

    const startProcessingFile = () => {
      setDefaultProgressValues();

      // Process ID is generated to track the translation process via SignalR and for cancellation if needed
      processIdRef.current = uuidv4();
      translationInProgressRef.current = false;

      translationStore.addProgressingFile(uploadFile);
      setProgressedFileResult(undefined);
      setIsSignalRActive(false);
      setIsFileProcessing(true);
    };

    const startUpload = async () => {
      try {
        abortControllerRef.current = new AbortController();

        const file = uploadFile.file;

        const requestInitOverrides: RequestInit | InitOverrideFunction = {
          signal: abortControllerRef.current.signal,
          body: file,
        };

        console.debug("[Translation] Starting file upload for translation:");
        const response: ApiResponse<string> = await translationService.translateFile(
          {
            processId: processIdRef.current!,
            fileName: encodeURIComponent(file.name),
            fileSizeInBytes: file.size,
            sourceLanguageCode: props.sourceLanguage.code,
            targetLanguageCode: props.targetLanguage.code,
            glossaryId: props.selectedGlossary?.glossaryId,
          },
          requestInitOverrides,
        );

        abortControllerRef.current = undefined;

        if (response.raw.ok) {
          console.debug(
            "[Translation] File uploaded successfully and translation started. Waiting for SignalR events...",
          );
        } else if (response.raw.status === 402) {
          cancelFileUploadAndOpenProcesses();

          handleBudgetLimitReached(response.raw);
          setProgressedFileResult({
            errorMessage: t(translationKeys.globalErrors.budgetErrors.outOfBudget),
          });
        } else {
          cancelFileUploadAndOpenProcesses();

          const error = await response.raw.json();
          console.error("[Translation] File upload failed. Error: ", error);

          setProgressedFileResult({
            errorMessage: t(translationKeys.upload.error.uploadFailedMarkdown),
          });
        }
      } catch (error: unknown) {
        cancelFileUploadAndOpenProcesses();

        if (error === 499) {
          console.debug("[Translation] File upload aborted");
        } else {
          console.error("[Translation] Error uploading file:", error);
          setProgressedFileResult({
            errorMessage: t(translationKeys.upload.error.uploadFailedMarkdown),
          });
        }
      }
    };

    const cancelFileProcessingInBackend = useCallback(async (processId: string) => {
      try {
        await translationService.cancelActiveTranslation({ processId });

        console.debug("[Translation] Translation file processing cancelled successfully.");
      } catch (error) {
        console.error("[Translation] Failed to cancel file processing", error);
      }
    }, []);

    const cancelFileUploadAndOpenProcesses = useCallback(() => {
      if (!(translationStore.isAnyFileProgressing && processIdRef.current)) {
        console.debug("[Translation] No process ID found when trying to cancel file upload and open processes.");
        return;
      }

      console.debug("[Translation] Cancel file upload and stop open signalR connections");

      abortControllerRef.current?.abort(499);
      translationStore.removeProgressingFile(uploadFile);

      if (translationInProgressRef.current) {
        cancelFileProcessingInBackend(processIdRef.current);
      }

      setDefaultProgressValues();
    }, [processIdRef.current]);

    const progressVisualization = (progress: IProgressVisualizationProps) => (
      <ProgressCircle
        progressTitles={progress.progressTitles}
        progressValues={progress.progressValues}
        cancelProgress={progress.cancelProgress}
      />
    );

    const progressHandlerMemo = useMemo(() => {
      if (!isFileProcessing) {
        return;
      }

      const processIdFilter = fieldNonexistentOrMatchingFilter("processId", processIdRef.current);

      const successCallback = (res: IFileProgressSuccess) => {
        const blob = blobFromByteArray(res.result, "[Translation]", uploadFile.file.type as TMimeType);
        if (!blob) {
          console.error("[Translation] Failed to create output file blob from response.");
          setProgressedFileResult({
            errorMessage: t(translationKeys.feature.translation.type.file.output.errorCreatingOutputFile),
          });
        } else {
          console.debug("[Translation] succeeded: ", res, blob);
          setProgressedFileResult({
            successMessage: t(translationKeys.upload.translation.success, {
              outputLanguage: t(props.targetLanguage.code),
            }),
            outputFile: blob,
          });
        }

        setDefaultProgressValues();
      };

      const errorCallback = (res: IFileProgressError) => {
        console.error("[Translation] failed: ", res);
        setProgressedFileResult({
          errorMessage: `${res.error}`,
        });

        setDefaultProgressValues();
      };

      const columns: IProgressColumn[] = [
        {
          titleKey: translationKeys.upload.progress.upload,
          progress: 0,
          hubName: "uploadfile",
          progressEndpoint: SignalREventNames.UploadFileProgress,
          successEndpoint: SignalREventNames.UploadFileSucceeded,
          errorEndpoint: SignalREventNames.UploadFileFailed,
          filter: [processIdFilter],
          successCallback: () => console.debug("[Translation] Upload completed successfully"),
          errorCallback: () => console.debug("[Translation] Upload failed"),
        },
        {
          titleKey: translationKeys.upload.progress.translate,
          progress: 0,
          hubName: "translation",
          progressEndpoint: SignalREventNames.TranslationProgress,
          successEndpoint: SignalREventNames.TranslationSucceeded,
          errorEndpoint: SignalREventNames.TranslationFailed,
          filter: [processIdFilter],
          progressCallback: () => {
            translationInProgressRef.current = true;
            console.debug("[Translation] Translation is in progress...");
          },
          successCallback,
          errorCallback,
        },
      ];

      return (
        <ProgressHandler
          progresscolumns={columns}
          cancelProgress={cancelFileUploadAndOpenProcesses}
          connectionSetupCompleted={() => setIsSignalRActive(true)}
          progressVisualizationTemplate={progressVisualization}
        />
      );
    }, [isFileProcessing]);

    const downloadFile = () => {
      if (!uploadFile) {
        throw new Error("Original file not available.");
      }
      if (!progressedFileResult?.outputFile) {
        throw new Error("No output file available for download.");
      }

      // Download the file
      const url = URL.createObjectURL(progressedFileResult.outputFile);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${props.targetLanguage.code.toUpperCase()}_${uploadFile.file.name}`;

      a.click();
      URL.revokeObjectURL(url);
    };

    const sourceComponent = () => (
      <div className="translation-file-upload">
        <FileUploadComponent
          files={translationStore.uploadFiles}
          showAllowedFileTypes={false}
          onChange={(files: any[]) => (translationStore.uploadFiles = files)}
          disabled={budgetStore.hasBudget === false}
          allowedFileTypes={allowedFileTypes}
          fileSizeLimitMB={100}
          maxConcurrentProcessedFiles={1}
          progressingFileIds={isFileProcessing && uploadFile ? [uploadFile.id] : []}
        />
      </div>
    );

    const targetComponentMemo = useMemo(
      () => (
        <div className="target-file-area-output">
          <div className="translation-output">
            {!!progressedFileResult?.outputFile && (
              <Fragment>
                <div className="title">
                  {t(fileTranslationKeys.output.success, {
                    outputLanguage: t(props.targetLanguage.labelKey ?? ""),
                  })}
                </div>

                <div
                  className="download-file"
                  onClick={downloadFile}
                  onKeyDown={event => event.key === "Enter" && downloadFile()}
                  aria-label="Download"
                  tabIndex={0}
                  role="button"
                >
                  <FontAwesomeIcon icon={faDownload} />
                </div>
              </Fragment>
            )}

            {!!progressedFileResult?.errorMessage && (
              <Fragment>
                <div className="title">{t(fileTranslationKeys.output.error)}</div>

                <div className="error-message">{progressedFileResult.errorMessage}</div>
              </Fragment>
            )}
          </div>
        </div>
      ),
      [progressedFileResult],
    );

    return (
      <TranslationContentLayout
        type={TranslationType.File}
        sourceArea={sourceComponent()}
        targetArea={!!progressedFileResult && targetComponentMemo}
        isTranslating={isFileProcessing}
        showTranslationButton={true}
        customLoadingTemplate={progressHandlerMemo}
        disabled={!uploadFile || isFileProcessing || !budgetStore.hasBudget}
        translate={() => !isFileProcessing && startProcessingFile()}
      />
    );
  },
);
